import React, { useEffect, useState } from "react";
import { X, Camera } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { fileToBase64 } from "@/lib/files";
import { Field, inputClass } from "@/components/AuthShell";
import { PrimaryButton } from "@/components/kit";

export default function DogFormDialog({ open, onClose, onSaved, dog }) {
  const editing = !!dog;
  const [form, setForm] = useState({ name: "", breed: "", age: "", weight: "", weight_unit: "lb", notes: "", photo_base64: null });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        dog
          ? {
              name: dog.name || "",
              breed: dog.breed || "",
              age: String(dog.age ?? ""),
              weight: String(dog.weight ?? ""),
              weight_unit: dog.weight_unit || "kg",
              notes: dog.notes || "",
              photo_base64: dog.photo_base64 || null,
            }
          : { name: "", breed: "", age: "", weight: "", weight_unit: "lb", notes: "", photo_base64: null }
      );
    }
  }, [open, dog]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await fileToBase64(file);
    setForm((f) => ({ ...f, photo_base64: b64 }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        breed: form.breed.trim() || "Unknown",
        age: parseFloat(form.age) || 0,
        weight: parseFloat(form.weight) || 0,
        weight_unit: form.weight_unit,
        notes: form.notes,
        photo_base64: form.photo_base64,
      };
      const saved = editing
        ? await apiFetch(`/dogs/${dog.id}`, { method: "PUT", body: JSON.stringify(body) })
        : await apiFetch("/dogs", { method: "POST", body: JSON.stringify(body) });
      toast.success(editing ? "Profile updated" : `${saved.name} added!`);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="dog-form-dialog"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-extrabold">{editing ? "Edit dog" : "Add a dog"}</h2>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#F2EFE9] flex items-center justify-center" data-testid="dog-form-close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={save}>
          <div className="flex flex-col items-center gap-2 mb-5">
            <label className="relative cursor-pointer" data-testid="dog-photo-picker">
              <div className="h-24 w-24 rounded-full bg-[#F2EFE9] overflow-hidden flex items-center justify-center border-2 border-[#E8E3DA]">
                {form.photo_base64 ? (
                  <img src={form.photo_base64.startsWith("data:") ? form.photo_base64 : `data:image/jpeg;base64,${form.photo_base64}`} alt="dog" className="h-full w-full object-cover" />
                ) : (
                  <Camera size={28} color="#9A938B" />
                )}
              </div>
              <span className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-[#E86A45] text-white flex items-center justify-center text-lg">+</span>
              <input type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
            </label>
            <label data-testid="dog-photo-camera" className="cursor-pointer inline-flex items-center gap-1.5 text-sm font-bold text-[#B54828]">
              <Camera size={15} /> Take a photo
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pickPhoto} />
            </label>
          </div>

          <Field label="Name">
            <input data-testid="dog-name-input" required value={form.name} onChange={set("name")} placeholder="e.g. Biscuit" className={inputClass} />
          </Field>
          <Field label="Breed">
            <input data-testid="dog-breed-input" value={form.breed} onChange={set("breed")} placeholder="e.g. Golden Retriever" className={inputClass} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Age (years)">
              <input data-testid="dog-age-input" inputMode="decimal" value={form.age} onChange={set("age")} placeholder="3" className={inputClass} />
            </Field>
            <Field label="Weight">
              <div className="flex gap-2">
                <input data-testid="dog-weight-input" inputMode="decimal" value={form.weight} onChange={set("weight")} placeholder={form.weight_unit === "kg" ? "12" : "26"} className={`${inputClass} flex-1 min-w-0`} />
                <div className="flex rounded-2xl overflow-hidden border border-[#E8E3DA] shrink-0">
                  {["lb", "kg"].map((u) => (
                    <button
                      key={u}
                      type="button"
                      data-testid={`dog-weight-unit-${u}`}
                      onClick={() => setForm((f) => ({ ...f, weight_unit: u }))}
                      className={`px-3 text-sm font-extrabold transition-colors ${form.weight_unit === u ? "bg-[#E86A45] text-white" : "bg-white text-[#4A4746]"}`}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>
            </Field>
          </div>
          <Field label="Notes (optional)">
            <textarea data-testid="dog-notes-input" value={form.notes} onChange={set("notes")} rows={2} placeholder="Allergies, medications, temperament…" className={inputClass} />
          </Field>

          <PrimaryButton data-testid="dog-save-btn" type="submit" loading={saving} className="w-full mt-2">
            {editing ? "Save changes" : "Add dog"}
          </PrimaryButton>
        </form>
      </div>
    </div>
  );
}
